import { inject, Injectable } from '@angular/core';
import { SocketService } from '@core/services/socket.service';

@Injectable({ providedIn: 'root' })
export class PricingApiService {
  private readonly socketService = inject(SocketService);

  /**
   * Raw socket.emit with manual timeout to avoid hangs on fresh reload
   * (pattern from MEMORY.md). Default 4 s.
   * Pass a higher timeoutMs for long ops, or `null` to wait until ACK and
   * only reject if the socket disconnects.
   */
  emit<T = any>(event: string, payload: unknown, timeoutMs: number | null = 4000): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const socket = this.socketService.socket;
      let timer: ReturnType<typeof setTimeout> | null = null;
      let onDisconnect: (() => void) | null = null;

      const cleanup = () => {
        if (timer) clearTimeout(timer);
        if (onDisconnect) socket.off('disconnect', onDisconnect);
      };

      if (timeoutMs === null) {
        onDisconnect = () => {
          cleanup();
          reject(new Error('socket disconnected'));
        };
        socket.on('disconnect', onDisconnect);
      } else {
        timer = setTimeout(() => {
          cleanup();
          reject(new Error('timeout'));
        }, timeoutMs);
      }

      socket.emit(event, payload, (first: any, second: any) => {
        cleanup();
        // BE uses single-arg callback: string = error, object = success data.
        if (typeof first === 'string') {
          reject(new Error(first));
          return;
        }
        if (first && typeof first === 'object') {
          resolve(first as T);
          return;
        }
        // Fallback: (err, data) two-arg pattern.
        if (first) reject(new Error('socket error'));
        else resolve(second as T);
      });
    });
  }
}
